import { useContext, useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import Lottie from 'lottie-react'
import { sileo } from 'sileo'
import { useLinkageBusinessAPI, useLinkageAmount } from '../LinkageCreationContext'
import { OrganizationAPI } from '../../../../../../../api/organization.api'
import GlobalContext from '../../../../../../../context/global-context'
import LoaderMain from '../../../../../../../assets/svgIcons/loaderMain.json'
import CustomButton from '../../../../../ui/CustomButton'
import ResizableModal from '../../../../../ui/ResizableModal'
import { Title } from '../../../../../ui'

const RELATED_COMPANY_ACCOUNTS = [
  {
    id_key: 341,
    name: 'Cuenta Corriente Empresa Relacionada 1',
    classification: '1-1-6-9 Cta Cte Empresa Relacionada 1'
  },
  {
    id_key: 342,
    name: 'Cuenta Corriente Empresa Relacionada 2',
    classification: '1-1-6-10 Cta Cte Empresa Relacionada 2'
  },
  {
    id_key: 343,
    name: 'Cuenta Corriente Empresa Relacionada 3',
    classification: '1-1-6-11 Cta Cte Empresa Relacionada 3'
  }
]

const getAccountId = account => account?.id_key ?? account?.id ?? null
const getAccountName = account => account?.name ?? account?.nombreCuenta ?? account?.nameCuenta ?? ''

const formatAmount = value => {
  const numericValue = String(value || '').replace(/\D/g, '')
  return numericValue ? new Intl.NumberFormat('es-CL').format(numericValue) : ''
}

const EmpresaRelacionada = ({
  open,
  setOpen,
  setSwitchDate,
  idLibro,
  folio,
  action = 'crear',
  type = 'egreso',
  title = 'Aporte Empresa Relacionada',
  model = 'aporte-ctacte-empresa',
  amountLabel = 'Monto Aporte'
}) => {
  const { currentBusiness } = useContext(GlobalContext)
  const businessAPI = useLinkageBusinessAPI()
  const organizationAPI = useMemo(() => new OrganizationAPI(), [])
  const [fecha, setFecha] = useState('')
  const [cuentaContableId, setCuentaContableId] = useState('')
  const [monto, setMonto, bankAmountLocked] = useLinkageAmount('')
  const [glosa, setGlosa] = useState('')
  const accounts = RELATED_COMPANY_ACCOUNTS
  const [data, setData] = useState({})
  const [loading, setLoading] = useState(false)

  const selectedAccount = accounts.find(
    account => String(getAccountId(account)) === String(cuentaContableId)
  )
  const classification = selectedAccount
    ? selectedAccount.classification
    : [data?.codigoCuenta, data?.nombreCuenta].filter(Boolean).join(' ')

  useEffect(() => {
    if (!open || !currentBusiness?.id) return

    let active = true
    const loadForm = async () => {
      setLoading(true)
      try {
        const dataResponse = idLibro
          ? await organizationAPI.getDataLibroContable(currentBusiness.id, idLibro, type.trim())
          : null
        if (!active) return
        setData(dataResponse?.data || {})
      } catch (error) {
        console.error(`Error al cargar ${title}:`, error)
      } finally {
        if (active) setLoading(false)
      }
    }

    loadForm()
    return () => {
      active = false
    }
  }, [currentBusiness?.id, idLibro, open, organizationAPI, title, type])

  useEffect(() => {
    if (!open) return
    setFecha(
      data?.fecha
        ? new Date(data.fecha).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0]
    )
    setMonto(data?.montoTotal?.toString() || '')
    setGlosa(data?.glosa || '')
    setCuentaContableId(
      data?.cuentaContableId ?? data?.idCuenta ?? data?.id_key ?? ''
    )
  }, [data, open])

  useEffect(() => {
    if (!open || cuentaContableId || accounts.length === 0) return
    const storedNames = [
      data?.nombreCodigoDocumento,
      data?.nombreComprobante,
      data?.nombreCuenta
    ].filter(Boolean)
    const matchingAccount = accounts.find(account =>
      storedNames.includes(getAccountName(account)) ||
      storedNames.includes(account.classification)
    )
    if (matchingAccount) setCuentaContableId(getAccountId(matchingAccount))
  }, [accounts, cuentaContableId, data, open])

  const handleClose = () => setOpen(false)

  const handleSubmit = async () => {
    if (!fecha || !cuentaContableId || !monto || !glosa.trim()) {
      sileo.error({
        title: 'Completa los campos',
        description: 'Fecha, cuenta contable, monto y detalle son obligatorios.'
      })
      return
    }

    const payload = {
      fecha,
      transaccionId: null,
      nombreComprobante: getAccountName(selectedAccount) || data?.nombreCuenta || null,
      nombreComprobanteId: null,
      glosa: glosa.trim(),
      respaldoId: null,
      monto: Number(monto),
      cuentaContableId: Number(cuentaContableId) || cuentaContableId
    }

    try {
      setLoading(true)
      if (action === 'modificar') {
        await organizationAPI.updateVinculacion(currentBusiness.id, idLibro, payload, model)
      } else {
        await businessAPI.postPagoArriendo(currentBusiness.id, payload, model)
      }
      setSwitchDate?.(true)
      handleClose()
    } catch (error) {
      console.error(`Error al guardar ${title}:`, error)
      sileo.error({ title: 'No se pudo guardar', description: 'Intenta nuevamente.' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <ResizableModal
      open={open}
      onClose={handleClose}
      footer={
        <>
          <CustomButton title='CANCELAR' color='primary' className='rounded-full' onClick={handleClose} />
          <CustomButton title='GRABAR VINCULACIÓN' color='accent2' className='rounded-full' onClick={handleSubmit} />
        </>
      }
    >
      {loading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <Lottie animationData={LoaderMain} loop autoplay style={{ width: 100, height: 100 }} />
        </Box>
      ) : (
        <div className='mt-4'>
          <Title className='mb-6 text-3xl font-sans'>{title}</Title>
          <div className='rounded-lg bg-white p-6 shadow-md'>
            <div className='grid w-full grid-cols-1 gap-4 md:grid-cols-2'>
              <label className='block text-sm font-medium text-gray-700'>
                Folio
                <input disabled value={folio || ''} className='mt-1 block w-full rounded-full border-gray-300 bg-gray-100 text-gray-500 shadow-sm' />
              </label>
              <label className='block text-sm font-medium text-gray-700'>
                Fecha
                <input type='date' value={fecha} onChange={event => setFecha(event.target.value)} className='mt-1 block w-full rounded-full border-gray-300 shadow-sm' />
              </label>
              <label className='block text-sm font-medium text-gray-700'>
                Cuenta Contable
                <select value={cuentaContableId} onChange={event => setCuentaContableId(event.target.value)} className='mt-1 block w-full rounded-full border-gray-300 bg-white shadow-sm'>
                  <option value='' disabled>Seleccione una cuenta</option>
                  {accounts.map(account => (
                    <option key={getAccountId(account)} value={getAccountId(account)}>
                      {getAccountName(account)}
                    </option>
                  ))}
                </select>
              </label>
              <label className='block text-sm font-medium text-gray-700'>
                Clasificación
                <input disabled value={classification} placeholder='Se asigna automáticamente' className='mt-1 block w-full rounded-full border-gray-300 bg-gray-100 text-gray-500 shadow-sm' />
              </label>
              <label className='block text-sm font-medium text-gray-700'>
                {amountLabel}
                <input disabled={bankAmountLocked} value={formatAmount(monto)} onChange={event => setMonto(event.target.value.replace(/\D/g, ''))} placeholder='$' className='mt-1 block w-full rounded-full border-gray-300 shadow-sm' />
              </label>
              <label className='block text-sm font-medium text-gray-700 md:col-span-2'>
                Detalle
                <textarea value={glosa} onChange={event => setGlosa(event.target.value)} maxLength={50} className='mt-1 block w-full rounded-md border-gray-300 shadow-sm' placeholder='Describe la vinculación que vamos a crear.' />
                <span className='mt-1 block text-xs text-gray-500'>{glosa.length}/50 caracteres</span>
              </label>
            </div>
          </div>
        </div>
      )}
    </ResizableModal>
  )
}

export const AporteEmpresaRelacionada = props => (
  <EmpresaRelacionada {...props} type={props.type || 'egreso'} />
)

export const DevolucionEmpresaRelacionada = props => (
  <EmpresaRelacionada
    {...props}
    type={props.type || 'ingreso'}
    title='Devolución Empresa Relacionada'
    model='devolucion-ctacte-empresa'
    amountLabel='Monto Devolución'
  />
)

export default EmpresaRelacionada
